import ApiRequest from "@/common/lib/apiRequest";
import * as web3 from "@solana/web3.js";

const mutationTypes = {
  SET_AVAILABLE_BALANCE: "SET_AVAILABLE_BALANCE",
  SET_PRICES: "SET_PRICES",
  SET_STAKED_INFO: "SET_STAKED_INFO",
  SET_DELEGATIONS: "SET_DELEGATIONS",
  SET_NETWORK_INFO: "SET_NETWORK_INFO",
  SET_CHAIN_INFO: "SET_CHAIN_INFO",
};

const state = {
  availableBalance: {
    fetching: false,
    result: null,
    error: null,
  },
  prices: {
    fetching: false,
    result: null,
    error: null,
  },
  stakedInfo: {
    fetching: false,
    result: null,
    error: null,
  },
  delegations: {
    fetching: false,
    result: null,
    error: null,
  },
  networkInfo: {
    fetching: false,
    result: {
      validatorAddress: "2NxEEbhqqj1Qptq5LXLbDTP5tLa9f7PqkU8zNgxbGU9P",
    },
    error: null,
  },
  chainInfo: {
    fetching: false,
    result: null,
    error: null,
  },
};

const actions = {
  async fetchRequiredInfo({ dispatch, rootGetters }) {
    await dispatch("fetchChainInfo");
    return Promise.all([
      dispatch("fetchAvailableBalance"),
      dispatch("fetchDelegations"),
    ]);
  },
  fetchNetworkInfo({ dispatch }) {
    return dispatch("fetchSwuStakedInfo");
  },
  async fetchAvailableBalance({ rootGetters, commit }) {
    const walletSelected = rootGetters["userWallets/walletSelected"];
    if (!walletSelected.address) {
      return;
    }
    commit(mutationTypes.SET_AVAILABLE_BALANCE, { fetching: true });
    try {
      const connection = new web3.Connection(
        process.env.VUE_APP_SOLANA_ENDPOINT
      );
      const nativeBalance = await connection.getBalance(
        new web3.PublicKey(walletSelected.address)
      );
      const result = {
        balance: nativeBalance / web3.LAMPORTS_PER_SOL,
      };
      commit(mutationTypes.SET_AVAILABLE_BALANCE, { result, fetching: false });
    } catch (error) {
      commit(mutationTypes.SET_AVAILABLE_BALANCE, { error, fetching: false });
    }
  },
  async fetchDelegations({ rootGetters, commit, state }) {
    const walletSelected = rootGetters["userWallets/walletSelected"];
    if (!walletSelected.address) {
      return;
    }
    const chainInfo = state.chainInfo.result;
    commit(mutationTypes.SET_DELEGATIONS, { fetching: true });
    try {
      const rpc = 'https://mainnet.helius-rpc.com/?api-key=2bfb175a-2e03-45c5-9dbb-29d2a3dc485c';
      const connection = new web3.Connection(rpc);
      const res = await connection.getParsedProgramAccounts(web3.StakeProgram.programId, {
        commitment: 'confirmed',
        filters: [
          {
            memcmp: {
              offset: 44,
              bytes: walletSelected.address,
            },
          },
        ],
      });
      const getDelegationStatus = (activationEpoch, deactivationEpoch) => {
        if (!chainInfo?.currentEpoch) {
          return;
        }
        const currentEpoch = +chainInfo.currentEpoch;
        if (deactivationEpoch == '18446744073709551615') {
          if (activationEpoch <= currentEpoch) {
            return 'Activating';
          } else {
            return 'Active';
          }
        } else {
          if (deactivationEpoch >= currentEpoch) {
            return 'Deactivating';
          } else {
            return 'Inactive';
          }
        }
      }
      const result = res?.map(r => {
        const { delegation } = r.account.data.parsed.info.stake;
        return {
          state: "STATE",
          account: r.pubkey.toString(),
          balance: r.account.lamports,
          stake: {
            delegated: delegation.stake,
            activationEpoch: delegation.activationEpoch,
            deactivationEpoch: delegation.deactivationEpoch,
            validator: {
              name: delegation.voter === '2NxEEbhqqj1Qptq5LXLbDTP5tLa9f7PqkU8zNgxbGU9P' ?
                'StakeWithUs' : delegation.voter,
              address: delegation.voter,
            },
          },
          status: getDelegationStatus(+delegation.activationEpoch, +delegation.deactivationEpoch),
          unstakeable:
            delegation.deactivationEpoch > chainInfo?.currentEpoch,
          withdrawable:
            delegation.deactivationEpoch < chainInfo?.currentEpoch,
        }
      }) || [];
      commit(mutationTypes.SET_DELEGATIONS, { result, fetching: false });
    } catch (error) {
      console.log(error);
      commit(mutationTypes.SET_DELEGATIONS, { error, fetching: false });
    }
  },
  async fetchAssetPriceChart({ commit }, { startTime, type = "h" } = {}) {
    commit(mutationTypes.SET_PRICES, { fetching: true });
    try {
      startTime = startTime || Date.now() - 86400000;
      startTime = Math.floor(startTime / 1000);
      const data = await ApiRequest.get(
        "/noneth/solana/token_prices/" + startTime,
        { params: { type } }
      );
      const result = getPriceChartData(data, type);
      commit(mutationTypes.SET_PRICES, { result, fetching: false });
      return result;
    } catch (error) {
      commit(mutationTypes.SET_PRICES, { error, fetching: false });
    }
  },
  async fetchSwuStakedInfo({ commit, getters }) {
    commit(mutationTypes.SET_STAKED_INFO, { fetching: true });
    try {
      const [result] = await Promise.all([
        ApiRequest.get("/noneth/solana/total_staked"),
      ]);
      commit(mutationTypes.SET_STAKED_INFO, { result, fetching: false });
    } catch (error) {
      commit(mutationTypes.SET_STAKED_INFO, { error, fetching: false });
    }
  },
  async fetchChainInfo({ commit }) {
    commit(mutationTypes.SET_CHAIN_INFO, { fetching: true });
    try {
      const connection = new web3.Connection(
        process.env.VUE_APP_SOLANA_ENDPOINT
      );
      const epochInfo = await connection.getEpochInfo();
      commit(mutationTypes.SET_CHAIN_INFO, { result: { currentEpoch: epochInfo.epoch }, fetching: false });
    } catch (error) {
      commit(mutationTypes.SET_CHAIN_INFO, { error, fetching: false });
    }
  },
  async getStakeRawTx({ state, rootGetters }, payload) {
    const walletSelected = rootGetters["userWallets/walletSelected"];
    const connection = new web3.Connection(process.env.VUE_APP_SOLANA_ENDPOINT);
    const fromPubkey = new web3.PublicKey(walletSelected.address);
    const votePubkey = new web3.PublicKey(
      state.networkInfo.result.validatorAddress
    );
    const newStakeAccount = web3.Keypair.generate();
    const stakePubkey = newStakeAccount.publicKey;
    const transaction = new web3.Transaction().add(
      web3.StakeProgram.createAccount({
        fromPubkey,
        stakePubkey,
        authorized: new web3.Authorized(fromPubkey, fromPubkey),
        lamports: payload.amount * web3.LAMPORTS_PER_SOL,
        lockup: new web3.Lockup(0, 0, fromPubkey),
      }),
      web3.StakeProgram.delegate({
        stakePubkey,
        authorizedPubkey: fromPubkey,
        votePubkey: votePubkey,
      })
    );
    transaction.feePayer = fromPubkey;
    transaction.recentBlockhash = (
      await connection.getLatestBlockhash()
    ).blockhash;
    transaction.sign(newStakeAccount);
    return transaction;
  },
  async getDeactivateRawTx({ rootGetters }, payload) {
    const walletSelected = rootGetters["userWallets/walletSelected"];
    const connection = new web3.Connection(process.env.VUE_APP_SOLANA_ENDPOINT);
    const fromPubkey = new web3.PublicKey(walletSelected.address);
    const stakePubkey = new web3.PublicKey(payload.stakeAccount);
    const transaction = new web3.Transaction().add(
      web3.StakeProgram.deactivate({
        stakePubkey,
        authorizedPubkey: fromPubkey,
      })
    );
    transaction.feePayer = fromPubkey;
    transaction.recentBlockhash = (
      await connection.getLatestBlockhash()
    ).blockhash;
    return transaction;
  },
  async getWithdrawRawTx({ rootGetters }, payload) {
    const walletSelected = rootGetters["userWallets/walletSelected"];
    const connection = new web3.Connection(process.env.VUE_APP_SOLANA_ENDPOINT);
    const fromPubkey = new web3.PublicKey(walletSelected.address);
    const stakePubkey = new web3.PublicKey(payload.stakeAccount);
    const transaction = new web3.Transaction().add(
      web3.StakeProgram.withdraw({
        stakePubkey,
        authorizedPubkey: fromPubkey,
        toPubkey: fromPubkey,
        lamports: payload.amount * web3.LAMPORTS_PER_SOL,
      })
    );
    transaction.feePayer = fromPubkey;
    transaction.recentBlockhash = (
      await connection.getLatestBlockhash()
    ).blockhash;
    return transaction;
  },
  async createTx({ rootGetters }, payload) {
    const walletSelected = rootGetters["userWallets/walletSelected"];
    return ApiRequest.post(
      `/noneth/solana/transaction/create/${walletSelected.address}`,
      {
        type: payload.type,
        tx_hash: payload.txHash,
        metadata: payload.metadata,
      }
    );
  },
  resetState() {},
};

const mutations = {
  [mutationTypes.SET_AVAILABLE_BALANCE](state, data) {
    state.availableBalance = {
      ...state.availableBalance,
      ...data,
    };
  },
  [mutationTypes.SET_PRICES](state, data) {
    state.prices = data;
  },
  [mutationTypes.SET_STAKED_INFO](state, data) {
    state.stakedInfo = {
      ...state.stakedInfo,
      ...data,
    };
  },
  [mutationTypes.SET_DELEGATIONS](state, data) {
    state.delegations = {
      ...state.delegations,
      ...data,
    };
  },
  [mutationTypes.SET_NETWORK_INFO](state, data) {
    state.networkInfo = {
      ...state.networkInfo,
      ...data,
    };
  },
  [mutationTypes.SET_CHAIN_INFO](state, data) {
    state.chainInfo = {
      ...state.chainInfo,
      ...data,
    };
  },
};

const getters = {
  stakedInfo: state => state.stakedInfo,
  availableBalance: state => state.availableBalance,
  delegations: state => state.delegations,
  networkInfo: state => state.networkInfo,
  chainInfo: state => state.chainInfo,
};

const getPriceChartData = (data, type) => {
  data = data.sort((a, b) => a.timestamp - b.timestamp);
  const result = [];
  for (let index = data.length - 1; index > -1; index -= 1) {
    const elem = data[index];
    result.unshift([elem.timestamp, Number(elem.usd)]);
  }
  if (result.length === 1) return [result[0], result[0]];
  return result;
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
